/**
 * Screen builders for the game shell. Each returns a detached element; the
 * app mounts one at a time. All track text (titles, descriptions, authors)
 * comes from files and local storage and is untrusted, so it only ever goes
 * through textContent (via h()), never markup.
 */
import { button, h } from '../editor/dom';
import type { TrackProgress } from '../gameplay/ProgressStore';
import { formatTime } from '../ui/Hud';
import { formatDelta } from '../ui/GameHud';
import { ACTION_LABELS, keyLabel, type Settings } from '../ui/Settings';
import { gameBindings } from '../ui/Settings';
import type { Action } from '../input/InputManager';
import type { CatalogTrack, TrackSource } from './catalog';
import type { RunResult } from './GameSession';
import { INELIGIBLE_TEXT, sectorLabel, sectors } from '../replay/RunTiming';
import { trackThumbnail } from './thumbnail';

export type Click = () => void;

function heading(title: string, sub?: string): HTMLElement {
  return h('header', { className: 'screen-head' }, h('h1', { textContent: title }), sub ? h('p', { className: 'lede', textContent: sub }) : null);
}

function back(label: string, onBack: Click): HTMLButtonElement {
  return button(`← ${label}`, onBack, { className: 'back', dataset: { nav: 'back' } });
}

// ---------------------------------------------------------------- main menu

export interface MenuActions {
  play: Click;
  replays: Click;
  create: Click;
  settings: Click;
  howTo: Click;
  credits: Click;
  /** Shown on touch-only devices instead of hiding the menu. */
  touchOnly: boolean;
  summary: { completed: number; total: number };
  /** Development builds: the ranked server's accepted runs. */
  devScores?: Click;
}

export function mainMenu(a: MenuActions): HTMLElement {
  const items: [string, string, Click, string][] = [
    ['Play', 'Choose a track and run it', a.play, 'play'],
    ['Replays', 'Watch, compare and share your runs', a.replays, 'replays'],
    ['Create Track', 'Build your own in the track editor', a.create, 'create'],
    ['Settings', 'Controls, video, audio, comfort', a.settings, 'settings'],
    ['How to Play', 'Movement, momentum and hazards', a.howTo, 'howto'],
  ];
  if (a.devScores) items.push(['Ranked scores', 'Development server: accepted ranked runs', a.devScores, 'scores']);
  const nav = h('nav', { className: 'menu-items', ariaLabel: 'Main menu' });
  for (const [label, sub, fn, id] of items) {
    const b = button('', fn, { className: id === 'play' ? 'menu-item primary' : 'menu-item', dataset: { menu: id } });
    b.append(h('span', { className: 'menu-label', textContent: label }), h('span', { className: 'menu-sub', textContent: sub }));
    nav.append(b);
  }
  const touch = a.touchOnly
    ? h('p', { className: 'compat-warning', role: 'alert', textContent: 'Xernous needs a keyboard and a mouse. This device looks touch-only, so the game will not be playable here; the menus still work.' })
    : null;
  return h(
    'section',
    { className: 'screen screen-menu', dataset: { screen: 'menu' } },
    menuScene(),
    h('div', { className: 'menu-brand' }, h('p', { className: 'menu-kicker', textContent: 'A movement game' }), h('h1', { className: 'wordmark', textContent: 'Xernous', dataset: { text: 'Xernous' } }), h('p', { className: 'menu-tag', textContent: 'Momentum, geometry and timing. Find the line, carry the speed, beat your time.' })),
    nav,
    touch,
    h('footer', { className: 'menu-foot' }, h('span', { textContent: `${a.summary.completed} of ${a.summary.total} tracks completed` }), button('Credits', a.credits, { className: 'link', dataset: { menu: 'credits' } }), h('span', { textContent: 'Alpha build · progress is stored in this browser only' })),
  );
}

/** The menu's backdrop: an endless outlined corridor drawn in CSS (no WebGL), drifting toward the viewer. */
function menuScene(): HTMLElement {
  return h(
    'div',
    { className: 'menu-scene', ariaHidden: 'true' },
    h('div', { className: 'scene-corridor' }, h('div', { className: 'scene-plane scene-floor' }), h('div', { className: 'scene-plane scene-ceiling' }), h('div', { className: 'scene-plane scene-left' }), h('div', { className: 'scene-plane scene-right' })),
  );
}

// ---------------------------------------------------------------- track browser

export interface BrowserActions {
  back: Click;
  select(track: CatalogTrack): void;
  play(track: CatalogTrack, mode: 'full-run' | 'practice'): void;
  edit(track: CatalogTrack): void;
  importFile(file: File): void;
  progress(track: CatalogTrack): TrackProgress;
  selected: string | null;
  mode: 'full-run' | 'practice';
  setMode(mode: 'full-run' | 'practice'): void;
  loadingLocal: boolean;
  notice: string;
}

const SECTION_TITLES: Record<TrackSource, string> = {
  builtin: 'Tracks',
  training: 'Training',
  local: 'Your tracks',
  developer: 'Developer levels',
};

function trackCard(t: CatalogTrack, p: TrackProgress, selected: boolean, onSelect: Click): HTMLButtonElement {
  const card = button('', onSelect, { className: `track-card${selected ? ' selected' : ''}${t.error ? ' broken' : ''}`, dataset: { track: t.key } });
  card.setAttribute('aria-pressed', String(selected));
  const thumb = h('span', { className: 'card-thumb' });
  if (t.level) thumb.append(trackThumbnail(t.level, t.title));
  else thumb.append(h('span', { className: 'thumb-missing', textContent: '!' }));
  const status = t.error ? 'Cannot be played' : p.best !== null ? `Best ${formatTime(p.best)}` : p.attempts ? 'Not completed yet' : 'New';
  card.append(
    thumb,
    h('span', { className: 'card-text' }, h('span', { className: 'card-title', textContent: t.title }), h('span', { className: `pill pill-${t.difficulty.toLowerCase()}`, textContent: t.difficulty }), h('span', { className: 'card-status', textContent: status })),
    ...(p.completions > 0 ? [h('span', { className: 'card-done', title: 'Completed', textContent: '✓' })] : []),
  );
  return card;
}

function trackDetail(t: CatalogTrack, p: TrackProgress, a: BrowserActions): HTMLElement {
  const big = h('div', { className: 'detail-thumb' });
  if (t.level) big.append(trackThumbnail(t.level, t.title));
  const facts = h('dl', { className: 'detail-facts' });
  const fact = (k: string, v: string): void => facts.append(h('div', {}, h('dt', { textContent: k }), h('dd', { textContent: v })));
  fact('Difficulty', t.difficulty);
  fact('Author', t.author || 'Unknown');
  fact('Personal best', formatTime(p.best));
  fact('Completions', String(p.completions));
  fact('Attempts', String(p.attempts));
  if (t.fingerprint) fact('Revision', t.fingerprint.slice(0, 12));
  if (t.source === 'developer') fact('Records', 'Kept per level version');
  const recent = p.recent.length
    ? h('p', { className: 'detail-recent', textContent: `Recent: ${p.recent.slice(-5).reverse().map((s) => formatTime(s)).join(' · ')}` })
    : null;
  const modes = h('div', { className: 'mode-switch', role: 'radiogroup', ariaLabel: 'Mode' });
  for (const [m, label, help] of [
    ['full-run', 'Full run', 'Timed and recorded. A death or pause ends the attempt.'],
    ['practice', 'Practice', 'Respawn at checkpoints. Times are not recorded.'],
  ] as const) {
    const b = button(label, () => a.setMode(m), { className: a.mode === m ? 'active' : '', title: help, dataset: { mode: m } });
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', String(a.mode === m));
    modes.append(b);
  }
  const play = button(t.error ? 'Cannot play' : 'Play', () => a.play(t, a.mode), { className: 'primary play-button', disabled: !!t.error, dataset: { action: 'play' } });
  const edit = t.source === 'local' ? button('Open in editor', () => a.edit(t), { className: 'secondary' }) : null;
  return h(
    'article',
    { className: 'track-detail', dataset: { detail: t.key } },
    big,
    h('h2', { textContent: t.title }),
    t.error ? h('p', { className: 'detail-error', role: 'alert', textContent: `This track can't be played: ${t.error}` }) : null,
    h('p', { className: 'detail-desc', textContent: t.description || 'No description.' }),
    facts,
    recent,
    h('p', { className: 'mode-help', textContent: a.mode === 'full-run' ? 'Full run: timed and recorded. A death or a pause ends the attempt; R restarts.' : 'Practice: respawn at the last checkpoint. Times are not recorded.' }),
    modes,
    h('div', { className: 'detail-actions' }, play, edit),
  );
}

export function trackBrowser(tracks: readonly CatalogTrack[], a: BrowserActions): HTMLElement {
  const list = h('div', { className: 'track-list' });
  const sel = tracks.find((t) => t.key === a.selected) ?? tracks.find((t) => t.source === 'builtin') ?? tracks[0] ?? null;
  for (const source of ['builtin', 'training', 'local', 'developer'] as const) {
    const items = tracks.filter((t) => t.source === source);
    const cards = items.map((t) => trackCard(t, a.progress(t), t === sel, () => a.select(t)));
    const extra: (HTMLElement | null)[] = [];
    if (source === 'local') {
      const input = h('input', { type: 'file', accept: '.json,.xtrack.json,application/json', className: 'visually-hidden' });
      input.addEventListener('change', () => {
        const f = input.files?.[0];
        if (f) a.importFile(f);
        input.value = '';
      });
      const pick = button('Import a track file…', () => input.click(), { className: 'secondary import', dataset: { action: 'import' } });
      extra.push(h('div', { className: 'section-tools' }, pick, input));
      if (a.loadingLocal) extra.push(h('p', { className: 'section-empty', textContent: 'Loading your tracks…' }));
      else if (!items.length) extra.push(h('p', { className: 'section-empty', textContent: 'Tracks you build in the editor or import appear here.' }));
    }
    if (!items.length && source !== 'local') continue;
    const group = source === 'developer' ? h('details', { className: 'track-section dev' }, h('summary', { textContent: `${SECTION_TITLES[source]} (${items.length})` }), ...cards) : h('section', { className: 'track-section' }, h('h2', { textContent: SECTION_TITLES[source] }), ...extra, ...cards);
    list.append(group);
  }
  return h(
    'section',
    { className: 'screen screen-tracks', dataset: { screen: 'tracks' } },
    h('div', { className: 'screen-bar' }, back('Menu', a.back), heading('Choose a track')),
    a.notice ? h('p', { className: 'browser-notice', role: 'status', textContent: a.notice }) : null,
    h('div', { className: 'browser' }, list, sel ? trackDetail(sel, a.progress(sel), a) : h('p', { textContent: 'No tracks.' })),
  );
}

// ---------------------------------------------------------------- how to play

export function howToPlay(settings: Readonly<Settings>, actions: { back: Click; training: Click }): HTMLElement {
  const b = gameBindings(settings.bindings);
  const keys = (a: Action): string => b[a].map(keyLabel).join(' / ') || 'unbound';
  const move = `${keys('forward')}, ${keys('left')}, ${keys('back')}, ${keys('right')}`;
  const topics: [string, string][] = [
    ['Move', `${move} to move, the mouse to look. Click the game to capture the mouse; Esc releases it and pauses.`],
    ['Jump', `${keys('jump')}. A jump keeps the speed you had on the ground. You can still jump for a moment after running off an edge, but don't rely on it.`],
    ['Momentum', 'Running on flat ground tops out at run speed. Going down a slope adds speed; going up one costs it. Speed above run speed fades slowly on the ground, and much more slowly in the air.'],
    ['Curves and slopes', 'Smooth curves keep your speed if you steer with them. Holding against your motion brakes hard.'],
    ['Air control', 'In the air, steering turns your motion without slowing it, but turns are gentler the faster you go. Line up before you leave the ground.'],
    ['Bunny-hop and strafe', `Jump again just as you land and the ground takes none of your speed. In the air, hold ${keys('left')} or ${keys('right')} and turn the mouse the same way: each hop gets a little faster. Holding a key without turning adds nothing.`],
    ['Slide and crouch', `${keys('crouch')} crouches. Crouch while running fast to slide: a burst of speed (once every 1.5 s), little friction, and downhill it keeps accelerating. Jump out of a slide to keep the speed. Crouch in the air to tuck your legs over a ledge.`],
    ['Walls', `Jump along a wall at speed with ${keys('forward')} held to run on it for a moment. Jump off a wall to kick away from it, harder the faster you hit it. You cannot kick off the same wall twice before touching the ground or another wall. On a slope too steep to stand on, strafe into it to surf along it.`],
    ['Hazards', 'Everything is drawn as outlines in the dark. Stone is outlined in your outline colour (white unless you change it in Settings → Video). Red outlines (with a dim red glow) are embers: lethal to touch, and so is falling into a pit. Cyan outlines are glide stone: you keep speed on it but turn and accelerate less.'],
    ['Restart', `${keys('restart')} restarts (from the last checkpoint in practice). ${keys('restartToStart')} always goes back to the start.`],
    ['Full run and practice', `A full run is timed and recorded: a death or a pause ends it. Practice respawns you at checkpoints and never records a time. ${keys('toggleMode')} switches between them.`],
    ['Routes', 'Most tracks have more than one way through. A wide gap is usually a hint: it is only possible with the speed you carry into it.'],
  ];
  return h(
    'section',
    { className: 'screen screen-howto', dataset: { screen: 'howto' } },
    h('div', { className: 'screen-bar' }, back('Menu', actions.back), heading('How to play', 'Short version: run, jump, keep your speed, and touch nothing red.')),
    h('div', { className: 'howto-grid' }, ...topics.map(([t, body]) => h('article', { className: t === 'Hazards' ? 'howto-card hazards' : 'howto-card' }, h('h2', { textContent: t }), h('p', { textContent: body })))),
    h(
      'div',
      { className: 'howto-cta' },
      h('p', { textContent: 'The Cells are four practice rooms, one skill each with a checkpoint: jumping pits, hopping ember hurdles, crouching under a gate, and a slide into a jump. Wall-runs come later, in Sunken Halls and the Ossuary. It all runs on the real movement engine; the HUD shows your speed (Settings → Comfort → HUD: Expanded shows more).' }),
      button('Open the Cells', actions.training, { className: 'primary', dataset: { action: 'training' } }),
    ),
    h('p', { className: 'howto-keys', textContent: `All keys: ${(['forward', 'back', 'left', 'right', 'jump', 'crouch', 'restart', 'restartToStart', 'toggleMode'] as Action[]).map((a) => `${ACTION_LABELS[a]}: ${keys(a)}`).join(' · ')} · Pause: Esc · Diagnostics: F3` }),
  );
}

export function credits(actions: { back: Click }): HTMLElement {
  const lines: [string, string][] = [
    ['Game, movement engine and track editor', 'Xernous project'],
    ['Tracks', 'Built with the Xernous track editor and its authoring tools'],
    ['Sound', 'Synthesised in the browser at play time (Web Audio); no recorded samples'],
    ['Libraries', 'three.js (MIT), Rapier physics (Apache-2.0)'],
  ];
  return h(
    'section',
    { className: 'screen screen-credits', dataset: { screen: 'credits' } },
    h('div', { className: 'screen-bar' }, back('Menu', actions.back), heading('Credits')),
    h('dl', { className: 'credits' }, ...lines.flatMap(([k, v]) => [h('dt', { textContent: k }), h('dd', { textContent: v })])),
  );
}

// ---------------------------------------------------------------- loading and errors

export function loadingScreen(title: string, step: string): HTMLElement {
  return h('section', { className: 'screen screen-loading', dataset: { screen: 'loading' }, ariaBusy: 'true' }, h('div', { className: 'loader', ariaHidden: 'true' }), h('h1', { textContent: title }), h('p', { className: 'lede', role: 'status', textContent: step }));
}

export function errorScreen(title: string, message: string, actions: [string, Click][]): HTMLElement {
  return h(
    'section',
    { className: 'screen screen-error', dataset: { screen: 'error' } },
    h('h1', { textContent: title }),
    h('p', { className: 'error-message', role: 'alert', textContent: message }),
    h('div', { className: 'error-actions' }, ...actions.map(([label, fn], i) => button(label, fn, { className: i === 0 ? 'primary' : 'secondary' }))),
  );
}

// ---------------------------------------------------------------- in game

export interface PauseActions {
  resume: Click;
  restart: Click;
  toggleMode: Click;
  settings: Click;
  tracks: Click;
  menu: Click;
}

export function readyPanel(track: CatalogTrack, mode: string, settings: Readonly<Settings>, onStart: Click, onTracks: Click, ranked: HTMLElement | null = null): HTMLElement {
  const b = gameBindings(settings.bindings);
  const k = (a: Action): string => b[a].map(keyLabel)[0] ?? '—';
  const start = button('Start', onStart, { className: 'primary', dataset: { action: 'start' } });
  return h(
    'div',
    { className: 'game-panel ready', dataset: { panel: 'ready' } },
    h('p', { className: 'panel-kicker', textContent: mode === 'full-run' ? 'Full run' : 'Practice' }),
    h('h2', { textContent: track.title }),
    h('p', { className: 'panel-help', textContent: `Leave the start zone to start the clock; reach the finish to stop it. ${k('forward')}${k('left')}${k('back')}${k('right')} move · ${k('jump')} jump · ${k('crouch')} crouch / slide · ${k('restart')} restart · Esc pause` }),
    ranked,
    h('div', { className: 'panel-actions' }, start, button('Tracks', onTracks, { className: 'secondary', dataset: { action: 'tracks' } })),
    h('p', { className: 'panel-foot', textContent: 'Click Start (or press Enter) to capture the mouse.' }),
  );
}

export function pausePanel(message: string, mode: string, a: PauseActions): HTMLElement {
  return h(
    'div',
    { className: 'game-panel pause', dataset: { panel: 'pause' }, role: 'dialog', ariaLabel: 'Paused' },
    h('h2', { textContent: 'Paused' }),
    message ? h('p', { className: 'panel-help', textContent: message }) : null,
    h(
      'div',
      { className: 'panel-actions vertical' },
      button('Resume', a.resume, { className: 'primary', dataset: { action: 'resume' } }),
      button('Restart run', a.restart, { className: 'secondary', dataset: { action: 'restart' } }),
      button(mode === 'full-run' ? 'Switch to practice' : 'Switch to full run', a.toggleMode, { className: 'secondary', dataset: { action: 'mode' } }),
      button('Settings', a.settings, { className: 'secondary', dataset: { action: 'settings' } }),
      button('Track selection', a.tracks, { className: 'secondary', dataset: { action: 'tracks' } }),
      button('Main menu', a.menu, { className: 'secondary', dataset: { action: 'menu' } }),
    ),
  );
}

/** The comparison line under the final time. */
export function improvementText(r: RunResult): string {
  if (!r.valid) return r.ineligible.includes('practice') ? 'Practice run: not recorded' : 'Not recorded';
  if (r.previousBest === null) return 'First completion';
  const d = r.seconds - r.previousBest;
  if (d < 0) return `New personal best · ${formatDelta(d)} s`;
  if (d === 0) return 'Matched your best';
  return `${formatDelta(d)} s behind your best`;
}

export interface ResultActions {
  retry: Click;
  next: Click | null;
  tracks: Click;
  menu: Click;
  /** Watch this run in the replay viewer. */
  watch: Click;
  /** Keep this run in Replays under a name. */
  save: Click;
  /** Outcome of the last save ('' for none). */
  note: string;
  /** The ranked submission's status (null when the run was not ranked). */
  ranked: HTMLElement | null;
}

export function resultsPanel(r: RunResult, track: CatalogTrack, interactive: boolean, a: ResultActions): HTMLElement {
  // Sectors of this run against the previous best run's sector (same landmarks only) and the best sector ever.
  const pbSectors = new Map(sectors(r.previousBestSplits, r.previousBest).map((s) => [s.key, s.time]));
  const rows = sectors(r.splits, r.seconds).map((s) => {
    const ref = pbSectors.get(s.key);
    const best = r.previousBestSectors[s.key];
    const isBest = r.valid && (best === undefined || s.time < best);
    return h(
      'tr',
      { className: isBest ? 'best-sector' : '' },
      h('th', { textContent: sectorLabel(s) }),
      h('td', { textContent: formatTime(s.time) }),
      h('td', { textContent: ref === undefined ? '—' : formatDelta(s.time - ref), className: ref === undefined ? '' : s.time <= ref ? 'ahead' : 'behind' }),
      h('td', { textContent: best === undefined ? '—' : formatTime(best) }),
    );
  });
  const splits = rows.length
    ? h('table', { className: 'splits' }, h('thead', {}, h('tr', {}, h('th', { textContent: 'Sector' }), h('th', { textContent: 'This run' }), h('th', { textContent: 'vs PB run' }), h('th', { textContent: 'Best sector' }))), h('tbody', {}, ...rows))
    : null;
  const eligibility = r.valid
    ? h('p', { className: 'result-eligible', dataset: { eligible: 'yes' }, textContent: r.newBest ? 'Recorded as your new personal best.' : 'Recorded: a valid full run.' })
    : h('ul', { className: 'result-eligible', dataset: { eligible: 'no' } }, ...r.ineligible.map((x) => h('li', { textContent: INELIGIBLE_TEXT[x], dataset: { reason: x } })));
  const actions = interactive
    ? h(
        'div',
        { className: 'panel-actions' },
        button('Retry', a.retry, { className: 'primary', dataset: { action: 'retry' } }),
        a.next ? button('Next track', a.next, { className: 'secondary', dataset: { action: 'next' } }) : null,
        button('Watch replay', a.watch, { className: 'secondary', dataset: { action: 'watch-run' } }),
        button('Save recording', a.save, { className: 'secondary', dataset: { action: 'save-run' } }),
        button('Track selection', a.tracks, { className: 'secondary', dataset: { action: 'tracks' } }),
        button('Main menu', a.menu, { className: 'secondary' }),
      )
    : h('p', { className: 'panel-foot', textContent: `R retry${a.next ? ' · Enter next track' : ''} · Esc for the menu` });
  return h(
    'div',
    { className: `game-panel results${interactive ? '' : ' compact'}${r.newBest && r.valid ? ' best' : ''}`, dataset: { panel: 'results' } },
    h('p', { className: 'panel-kicker', textContent: track.title }),
    h('p', { className: 'result-time', textContent: formatTime(r.seconds) }),
    h('p', { className: 'result-compare', textContent: improvementText(r) }),
    r.previousBest !== null ? h('p', { className: 'result-prev', textContent: `${r.newBest ? 'Previous best' : 'Personal best'} ${formatTime(r.previousBest)}` }) : null,
    eligibility,
    a.ranked,
    interactive ? splits : null,
    actions,
    interactive && a.note ? h('p', { className: 'panel-help', role: 'status', dataset: { note: 'save' }, textContent: a.note }) : null,
  );
}
